import { ViewMode, VersionViewState } from './contentTypes'

/**
 * State interface for ContentDetails component
 */
export interface ContentDetailsState {
    // UI states
    loading: boolean
    editMode: boolean
    viewMode: ViewMode
    
    // Error handling
    error: string | null
    
    // Version control
    versionView: VersionViewState
    newVersionNumber?: number
    
    // Modal states
    isVersionHistoryOpen: boolean
    isDataModalOpen: boolean
    isEditModalOpen: boolean
    isPublishHelpOpen: boolean
    
    // Platform connection
    platformConnected: boolean
    isCheckingConnection: boolean
    
    // Chat
    shouldFocusChat: boolean
}

/**
 * Initial state for ContentDetails component
 */
export const initialState: ContentDetailsState = {
    // UI states
    loading: false,
    editMode: false,
    viewMode: 'preview',
    
    // Error handling
    error: null,
    
    // Version control
    versionView: {
        isViewingVersion: false,
        versionNumber: undefined
    },
    newVersionNumber: undefined,
    
    // Modal states
    isVersionHistoryOpen: false,
    isDataModalOpen: false,
    isEditModalOpen: false,
    isPublishHelpOpen: false,
    
    // Platform connection
    platformConnected: false,
    isCheckingConnection: false,
    
    // Chat
    shouldFocusChat: false
}

/**
 * Actions for ContentDetails reducer
 */
export type ContentDetailsAction =
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'TOGGLE_EDIT_MODE' }
    | { type: 'SET_EDIT_MODE'; payload: boolean }
    | { type: 'TOGGLE_VIEW_MODE' }
    | { type: 'SET_VIEW_MODE'; payload: ViewMode }
    | { type: 'VIEW_VERSION'; payload: number }
    | { type: 'RESET_VERSION_VIEW' }
    | { type: 'SET_NEW_VERSION'; payload?: number }
    | { type: 'TOGGLE_VERSION_HISTORY' }
    | { type: 'TOGGLE_DATA_MODAL' }
    | { type: 'TOGGLE_EDIT_MODAL' }
    | { type: 'TOGGLE_PUBLISH_HELP' }
    | { type: 'SET_PLATFORM_CONNECTED'; payload: boolean }
    | { type: 'SET_CHECKING_CONNECTION'; payload: boolean }
    | { type: 'SET_SHOULD_FOCUS_CHAT'; payload: boolean }

/**
 * Reducer for ContentDetails component state
 */
export function contentDetailsReducer(
    state: ContentDetailsState,
    action: ContentDetailsAction
): ContentDetailsState {
    switch (action.type) {
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload
            }
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload
            }
        case 'TOGGLE_EDIT_MODE':
            return {
                ...state,
                editMode: !state.editMode
            }
        case 'SET_EDIT_MODE':
            return {
                ...state,
                editMode: action.payload
            }
        case 'TOGGLE_VIEW_MODE':
            return {
                ...state,
                viewMode: state.viewMode === 'preview' ? 'improve' : 'preview',
                shouldFocusChat: state.viewMode === 'preview' // Focus chat when switching to improve mode
            }
        case 'SET_VIEW_MODE':
            return {
                ...state,
                viewMode: action.payload
            }
        case 'VIEW_VERSION':
            return {
                ...state,
                versionView: {
                    isViewingVersion: true,
                    versionNumber: action.payload
                }
            }
        case 'RESET_VERSION_VIEW':
            return {
                ...state,
                versionView: {
                    isViewingVersion: false,
                    versionNumber: undefined
                }
            }
        case 'SET_NEW_VERSION':
            return {
                ...state,
                newVersionNumber: action.payload
            }
        case 'TOGGLE_VERSION_HISTORY':
            return {
                ...state,
                isVersionHistoryOpen: !state.isVersionHistoryOpen
            }
        case 'TOGGLE_DATA_MODAL':
            return {
                ...state,
                isDataModalOpen: !state.isDataModalOpen
            }
        case 'TOGGLE_EDIT_MODAL':
            return {
                ...state,
                isEditModalOpen: !state.isEditModalOpen
            }
        case 'TOGGLE_PUBLISH_HELP':
            return {
                ...state,
                isPublishHelpOpen: !state.isPublishHelpOpen
            }
        case 'SET_PLATFORM_CONNECTED':
            return {
                ...state,
                platformConnected: action.payload
            }
        case 'SET_CHECKING_CONNECTION':
            return {
                ...state,
                isCheckingConnection: action.payload
            }
        case 'SET_SHOULD_FOCUS_CHAT':
            return {
                ...state,
                shouldFocusChat: action.payload
            }
        default:
            return state
    }
}